import {
  formatBakuMutuLine,
  formatJenisPengukuranLine,
  parseStoredParameterUjiDocumentIds,
  type StrapiBakuMutuMini,
  type StrapiJenisPengukuranMini,
} from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { dynamicRelation } from '../../components/surveyScheduleCalendar/utils';
import {
  quotationFormGroupTotalRecurringCount,
  recurringIntervalLabel,
  type QuotationContractHeaderInput,
} from '../QuotationsPage/QuotationFormRoute/quotationRecurring';

export type SampleLineScheduleParameterItem = {
  documentId: string;
  nama_parameter: string | null;
  simbol: string | null;
};

export type SampleLineScheduleDetailRow = {
  label: string;
  value: string;
  kind?: 'text' | 'simbols' | 'parameters';
  /** Dipakai saat `kind === 'parameters'` untuk render KaTeX via ChemParameterUjiCell. */
  parameterItems?: SampleLineScheduleParameterItem[];
};

export type SampleLineInGroupDetail = {
  lineIndex: number;
  label: string;
  details: SampleLineScheduleDetailRow[];
  /** Ringkas untuk summary accordion (tanpa expand). */
  samplePointCountLabel: string;
  hargaLabel: string;
  parameterSimbols?: string[];
  parameterItems?: SampleLineScheduleParameterItem[];
};

export type UnscheduledSampleGroupOption = {
  quotationGroupDocumentId: string;
  /** Identitas radio saat satu group punya beberapa event kontrak. */
  selectionKey?: string;
  quotationRecurringEventDocumentId?: string;
  /** Jadwal yang sudah terikat event ini, kalau ada. */
  scheduleDocumentId?: string;
  label: string;
  planDocumentIds: string[];
  groupDetails: SampleLineScheduleDetailRow[];
  sampleLines: SampleLineInGroupDetail[];
  /** Group tidak bisa dipilih (mis. nomor job sudah terbit). */
  disabled?: boolean;
  disabledReason?: string;
};

export function sampleGroupOptionSelectionKey(option: UnscheduledSampleGroupOption): string {
  return option.selectionKey?.trim() || option.quotationGroupDocumentId;
}

/** Kontrak: modal slot hanya memakai group/event yang sudah terikat ke jadwal ini. */
export function contractSampleGroupOptionForSchedule(
  options: UnscheduledSampleGroupOption[],
  scheduleDocumentId: string,
  recurringEventDocumentId?: string | null,
): UnscheduledSampleGroupOption[] {
  const scheduleId = scheduleDocumentId.trim();
  const eventId = String(recurringEventDocumentId ?? '').trim();
  const bySchedule = scheduleId
    ? options.filter((option) => option.scheduleDocumentId?.trim() === scheduleId)
    : [];
  if (bySchedule.length > 0) return bySchedule;
  if (!eventId) return [];
  return options.filter((option) => option.quotationRecurringEventDocumentId?.trim() === eventId);
}

/** @deprecated Use UnscheduledSampleGroupOption */
export type UnscheduledSampleLineOption = UnscheduledSampleGroupOption & {
  sampleLineDocumentId: string;
  planDocumentId: string;
  lineIndex: number;
  details: SampleLineScheduleDetailRow[];
};

/** Nama pelanggan di kartu/list: group (konsultan) lebih dulu, lalu header quotation. */
export function scheduleDisplayCustomerName(schedule: {
  quotation_group?: { customer?: { name?: string | null } | null } | null;
  quotation?: { customer?: { name?: string | null } | null } | null;
} | null | undefined): string {
  return (
    String(schedule?.quotation_group?.customer?.name ?? '').trim() ||
    String(schedule?.quotation?.customer?.name ?? '').trim()
  );
}

export type QuotationScheduleGroupMeta = {
  is_contract: boolean;
  is_consultant_quote: boolean;
  contract_period_start_date?: string | null;
  contract_period_end_date?: string | null;
};

const RECURRING_TYPE_LABEL: Record<string, string> = {
  weekly: 'Mingguan',
  monthly: 'Bulanan',
  yearly: 'Tahunan',
};

function formatIdrAmount(value: unknown): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `Rp ${n.toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
}

function formatDateId(iso?: string | null): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('id-ID');
}

function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function relationMany(raw: Record<string, unknown>, field: string): Record<string, unknown>[] {
  const value = raw[field];
  if (!Array.isArray(value)) return [];
  return value.filter((v) => v && typeof v === 'object') as Record<string, unknown>[];
}

function sampleLineLabel(line: Record<string, unknown> | null, lineIndex: number): string {
  if (!line) return `Sample line ${lineIndex + 1}`;
  const jenis = dynamicRelation(line as DynamicEntityRow, 'jenis_pengukuran');
  const jenisLabel = formatJenisPengukuranLine(jenis as StrapiJenisPengukuranMini | null);
  if (jenisLabel) return jenisLabel;
  return `Sample line ${lineIndex + 1}`;
}

function parameterItemsFromLine(line: Record<string, unknown>): SampleLineScheduleParameterItem[] {
  const populated = relationMany(line, 'parameter_ujis');
  const items: SampleLineScheduleParameterItem[] = [];
  const seen = new Set<string>();
  for (const row of populated) {
    const documentId = String(row.documentId ?? '').trim();
    if (!documentId || seen.has(documentId)) continue;
    seen.add(documentId);
    items.push({
      documentId,
      nama_parameter: row.nama_parameter != null ? String(row.nama_parameter) : null,
      simbol: row.simbol != null ? String(row.simbol) : null,
    });
  }
  return items;
}

function parameterSimbolsFromLine(line: Record<string, unknown>): string[] {
  return parameterItemsFromLine(line)
    .map((row) => (row.simbol ?? '').trim())
    .filter(Boolean);
}

function parameterCountFromLine(line: Record<string, unknown>): number {
  const items = parameterItemsFromLine(line);
  if (items.length) return items.length;
  const raw = line.parameter_uji;
  if (typeof raw === 'string') return parseStoredParameterUjiDocumentIds(raw).length;
  if (Array.isArray(raw)) return raw.length;
  return 0;
}

function samplePointCountLabelFromLine(line: Record<string, unknown> | null): string {
  if (!line) return '—';
  if (line.sample_point_count != null && Number.isFinite(Number(line.sample_point_count))) {
    return String(Math.max(1, Number(line.sample_point_count)));
  }
  return '—';
}

function buildSampleLinePickerDetails(
  line: Record<string, unknown> | null,
): SampleLineScheduleDetailRow[] {
  if (!line) return [];

  const jenis = dynamicRelation(line as DynamicEntityRow, 'jenis_pengukuran');
  const baku = dynamicRelation(line as DynamicEntityRow, 'baku_mutu');
  const parameterItems = parameterItemsFromLine(line);
  const parameterCount = parameterCountFromLine(line);
  const titik = samplePointCountLabelFromLine(line);

  const details: SampleLineScheduleDetailRow[] = [
    {
      label: 'Jenis pengukuran',
      value: formatJenisPengukuranLine(jenis as StrapiJenisPengukuranMini | null) || '—',
    },
    {
      label: 'Baku mutu',
      value: formatBakuMutuLine(baku as StrapiBakuMutuMini | null) || '—',
    },
    {
      label: 'Parameter uji',
      value: parameterCount > 0 ? `${parameterCount} parameter` : '—',
      ...(parameterItems.length
        ? { kind: 'parameters' as const, parameterItems }
        : {}),
    },
    {
      label: 'Jumlah titik',
      value: titik,
    },
    { label: 'Harga', value: formatIdrAmount(line.harga) },
  ];

  return details;
}

function buildSampleLineDetails(
  line: Record<string, unknown> | null,
  lineIndex: number,
  opts?: { includeGroupRecurring?: boolean },
): Pick<SampleLineInGroupDetail, 'details' | 'parameterSimbols' | 'parameterItems'> {
  if (!line) {
    return {
      details: [{ label: 'Baris', value: String(lineIndex + 1) }],
    };
  }

  const jenis = dynamicRelation(line as DynamicEntityRow, 'jenis_pengukuran');
  const baku = dynamicRelation(line as DynamicEntityRow, 'baku_mutu');
  const parameterItems = parameterItemsFromLine(line);
  const parameterCount = parameterCountFromLine(line);
  const parameterSimbols = parameterSimbolsFromLine(line);

  const details: SampleLineScheduleDetailRow[] = [
    { label: 'Baris', value: String(lineIndex + 1) },
    {
      label: 'Jenis pengukuran',
      value: formatJenisPengukuranLine(jenis as StrapiJenisPengukuranMini | null) || '—',
    },
    {
      label: 'Baku mutu',
      value: formatBakuMutuLine(baku as StrapiBakuMutuMini | null) || '—',
    },
    {
      label: 'Jumlah titik',
      value:
        line.sample_point_count != null && Number.isFinite(Number(line.sample_point_count))
          ? String(Math.max(1, Number(line.sample_point_count)))
          : '—',
    },
    { label: 'Harga', value: formatIdrAmount(line.harga) },
    {
      label: 'Parameter uji',
      value: parameterCount > 0 ? `${parameterCount} parameter` : '—',
      ...(parameterItems.length
        ? { kind: 'parameters' as const, parameterItems }
        : {}),
    },
  ];

  if (opts?.includeGroupRecurring !== false) {
    const group = dynamicRelation(line as DynamicEntityRow, 'quotation_group');
    if (group) {
      details.push(...buildGroupDetails(group).filter((row) => row.label !== 'Pelanggan group'));
    }
  }

  return {
    details,
    ...(parameterSimbols.length ? { parameterSimbols } : {}),
    ...(parameterItems.length ? { parameterItems } : {}),
  };
}

function contractHeaderFromMeta(meta: QuotationScheduleGroupMeta): QuotationContractHeaderInput {
  return {
    contract_period_start_date: meta.contract_period_start_date ?? '',
    contract_period_end_date: meta.contract_period_end_date ?? '',
  };
}

function buildGroupDetails(group: Record<string, unknown>): SampleLineScheduleDetailRow[] {
  const details: SampleLineScheduleDetailRow[] = [];

  const customer = dynamicRelation(group as DynamicEntityRow, 'customer');
  const customerName = String(customer?.name ?? '').trim();
  if (customerName) {
    details.push({ label: 'Pelanggan group', value: customerName });
  }

  const recurringType = String(group.recurring_type ?? '').trim();
  if (recurringType) {
    details.push({
      label: 'Recurring',
      value: RECURRING_TYPE_LABEL[recurringType] ?? recurringType,
    });
  }
  if (group.recurring_interval != null && Number.isFinite(Number(group.recurring_interval))) {
    details.push({
      label: 'Interval recurring',
      value: String(Math.max(1, Number(group.recurring_interval))),
    });
  }
  if (group.recurring_start_date) {
    details.push({
      label: 'Mulai recurring',
      value: formatDateId(String(group.recurring_start_date)),
    });
  }
  if (
    group.recurring_count_override != null &&
    Number.isFinite(Number(group.recurring_count_override))
  ) {
    details.push({
      label: 'Jumlah recurring',
      value: String(Math.max(1, Number(group.recurring_count_override))),
    });
  }
  const events = relationMany(group, 'quotation_recurring_events');
  if (events.length) {
    details.push({
      label: 'Event recurring',
      value: `${events.length} jadwal`,
    });
  }

  return details;
}

export function quotationScheduleGroupMetaFromQuotation(
  quotation: Record<string, unknown> | DynamicEntityRow | null | undefined,
): QuotationScheduleGroupMeta | null {
  if (!quotation || typeof quotation !== 'object') return null;
  const is_contract = Boolean(quotation.is_contract);
  const is_consultant_quote = Boolean(quotation.is_consultant_quote);
  if (!is_contract && !is_consultant_quote) return null;
  return {
    is_contract,
    is_consultant_quote,
    contract_period_start_date:
      quotation.contract_period_start_date != null
        ? String(quotation.contract_period_start_date)
        : null,
    contract_period_end_date:
      quotation.contract_period_end_date != null
        ? String(quotation.contract_period_end_date)
        : null,
  };
}

function groupRecordFromSchedule(schedule: DynamicEntityRow | null): Record<string, unknown> | null {
  if (!schedule) return null;
  const fromSchedule = dynamicRelation(schedule, 'quotation_group');
  if (fromSchedule) return fromSchedule as Record<string, unknown>;
  const plans = schedule.sampling_plans;
  if (Array.isArray(plans)) {
    for (const plan of plans) {
      if (!plan || typeof plan !== 'object') continue;
      const fromPlan = dynamicRelation(plan as DynamicEntityRow, 'quotation_group');
      if (fromPlan) return fromPlan as Record<string, unknown>;
    }
  }
  return null;
}

export function buildGroupLabel(
  group: Record<string, unknown>,
  groupIndex: number,
  meta: QuotationScheduleGroupMeta,
): string {
  const groupNum = groupIndex + 1;
  if (meta.is_consultant_quote) {
    const customer = dynamicRelation(group as DynamicEntityRow, 'customer');
    const name = String(customer?.name ?? '').trim();
    return name || `Group ${groupNum}`;
  }
  if (meta.is_contract) {
    const rt = String(group.recurring_type ?? '').trim();
    const intervalParsed = Number(group.recurring_interval);
    const interval = Number.isFinite(intervalParsed) && intervalParsed >= 1 ? intervalParsed : 1;
    const freqLabel =
      rt === 'weekly' || rt === 'monthly' || rt === 'yearly'
        ? capitalizeFirst(recurringIntervalLabel(rt, interval))
        : '—';
    const count =
      quotationFormGroupTotalRecurringCount(
        {
          recurring_type: rt as 'weekly' | 'monthly' | 'yearly' | '',
          recurring_interval: String(interval),
          recurring_count_override:
            group.recurring_count_override != null
              ? String(group.recurring_count_override)
              : '',
          recurring_start_date: group.recurring_start_date
            ? String(group.recurring_start_date).slice(0, 10)
            : '',
        },
        contractHeaderFromMeta(meta),
      ) ?? 1;
    return `Group ${groupNum} — ${freqLabel} · ${count}×`;
  }
  return `Group ${groupNum}`;
}

/**
 * Nomor Group 1, Group 2, … sama dengan modal quotation: urutan `createdAt` naik.
 * Tanpa `createdAt`, urutan documentId dipakai sebagai cadangan.
 */
export function groupIndexByCreationOrder(
  groups: Array<{ documentId?: string | null; createdAt?: string | null }>,
): Map<string, number> {
  const createdAtById = new Map<string, string>();
  for (const group of groups) {
    const id = String(group.documentId ?? '').trim();
    if (!id) continue;
    const createdAt = String(group.createdAt ?? '').trim();
    const existing = createdAtById.get(id);
    if (existing == null || (existing === '' && createdAt)) {
      createdAtById.set(id, createdAt);
    }
  }
  const ordered = [...createdAtById.entries()].sort((a, b) => {
    if (a[1] && b[1] && a[1] !== b[1]) return a[1] < b[1] ? -1 : 1;
    if (a[1] !== b[1]) return a[1] ? -1 : 1;
    return a[0].localeCompare(b[0]);
  });
  return new Map(ordered.map(([id], index) => [id, index]));
}

/** Indeks group per quotation dari daftar sampling plan (urut createdAt group). */
export function quotationGroupIndexFromPlans(plans: DynamicEntityRow[]): Map<string, number> {
  const groups: Array<{ documentId?: string | null; createdAt?: string | null }> = [];
  for (const plan of plans) {
    const group = planGroupRecord(plan);
    if (!group) continue;
    groups.push({
      documentId: String(group.documentId ?? ''),
      createdAt: group.createdAt != null ? String(group.createdAt) : null,
    });
  }
  return groupIndexByCreationOrder(groups);
}

/** Label group untuk kartu penjadwalan (quotation kontrak/konsultan). */
export function buildGroupLabelForSchedule(
  schedule: DynamicEntityRow | null,
  quotation: Record<string, unknown> | DynamicEntityRow | null | undefined,
  groupIndex = 0,
): string | null {
  const meta = quotationScheduleGroupMetaFromQuotation(quotation);
  if (!meta) return null;
  const group = groupRecordFromSchedule(schedule);
  if (!group) return null;
  return buildGroupLabel(group, groupIndex, meta);
}

function planGroupDocumentId(plan: DynamicEntityRow): string {
  const fromPlan = dynamicRelation(plan, 'quotation_group');
  if (fromPlan?.documentId) return String(fromPlan.documentId).trim();
  const sampleLine = dynamicRelation(plan, 'quotation_sample_information') as DynamicEntityRow | null;
  const fromLine = sampleLine ? dynamicRelation(sampleLine, 'quotation_group') : null;
  return String(fromLine?.documentId ?? '').trim();
}

function planGroupRecord(plan: DynamicEntityRow): Record<string, unknown> | null {
  const fromPlan = dynamicRelation(plan, 'quotation_group');
  if (fromPlan) return fromPlan as Record<string, unknown>;
  const sampleLine = dynamicRelation(plan, 'quotation_sample_information') as DynamicEntityRow | null;
  const fromLine = sampleLine ? dynamicRelation(sampleLine, 'quotation_group') : null;
  return fromLine ? (fromLine as Record<string, unknown>) : null;
}

function planJobIssued(plan: DynamicEntityRow): boolean {
  const wo = dynamicRelation(plan, 'work_order');
  return Boolean(String(wo?.job_number ?? '').trim());
}

function planScheduleDocumentId(plan: DynamicEntityRow): string {
  const schedule = dynamicRelation(plan, 'sampling_schedule');
  return String(schedule?.documentId ?? '').trim();
}

function planHasActiveSchedule(plan: DynamicEntityRow): boolean {
  const schedule = dynamicRelation(plan, 'sampling_schedule');
  const scheduleDocId = String(schedule?.documentId ?? '').trim();
  if (!scheduleDocId) return false;
  const scheduleStatus = String(schedule?.progress_status ?? '').trim();
  return scheduleStatus !== 'cancelled' && scheduleStatus !== 'work_order_created';
}

/**
 * Group customer yang jadi jadwal aktif saat membuka quotation konsultan/kontrak:
 * group yang belum dijadwalkan dulu, lalu yang belum WO, lalu group pertama.
 */
export function pickActiveQuotationGroupDocumentId(
  groups: Array<{ documentId?: string | null }>,
  plans: DynamicEntityRow[] = [],
): string {
  const groupIds = [
    ...new Set(groups.map((group) => String(group.documentId ?? '').trim()).filter(Boolean)),
  ];
  if (!groupIds.length) return '';

  const unscheduled = groupIds.find((groupId) => {
    const groupPlans = plans.filter((plan) => planGroupDocumentId(plan) === groupId);
    if (groupPlans.some((plan) => planJobIssued(plan))) return false;
    return !groupPlans.some((plan) => planHasActiveSchedule(plan));
  });
  if (unscheduled) return unscheduled;

  const withoutWorkOrder = groupIds.find((groupId) => {
    const groupPlans = plans.filter((plan) => planGroupDocumentId(plan) === groupId);
    if (!groupPlans.length) return true;
    return !groupPlans.every((plan) => planJobIssued(plan));
  });
  return withoutWorkOrder || groupIds[0];
}

function groupDisabledReason(
  groupPlans: DynamicEntityRow[],
  currentScheduleDocumentId: string,
): string | null {
  for (const plan of groupPlans) {
    const schedule = dynamicRelation(plan, 'sampling_schedule');
    const scheduleDocId = String(schedule?.documentId ?? '').trim();
    if (!scheduleDocId || scheduleDocId === currentScheduleDocumentId) continue;
    const scheduleStatus = String(schedule?.progress_status ?? '').trim();
    if (scheduleStatus === 'cancelled') continue;
    return 'Sudah dijadwalkan di penjadwalan lain';
  }

  return null;
}

function isPlanBlockedOnOtherSchedule(
  plan: DynamicEntityRow,
  currentScheduleDocumentId: string,
): boolean {
  const schedule = dynamicRelation(plan, 'sampling_schedule');
  const scheduleDocId = String(schedule?.documentId ?? '').trim();
  if (!scheduleDocId || scheduleDocId === currentScheduleDocumentId) return false;
  const scheduleStatus = String(schedule?.progress_status ?? '').trim();
  if (scheduleStatus === 'cancelled') return false;
  return true;
}

function isPlanAvailableForSchedule(
  plan: DynamicEntityRow,
  currentScheduleDocumentId: string,
  reservedPlanDocumentIds: Set<string>,
): boolean {
  const planDocId = String(plan.documentId ?? '').trim();
  if (!planDocId) return false;
  if (reservedPlanDocumentIds.has(planDocId)) return false;
  if (isPlanBlockedOnOtherSchedule(plan, currentScheduleDocumentId)) return false;
  return true;
}

/** Group sudah dijadwalkan di penjadwalan ini (plan terhubung + tim survey sudah ada). */
function isGroupHiddenAsScheduled(
  groupPlans: DynamicEntityRow[],
  currentScheduleDocumentId: string,
  reservedPlanDocumentIds: Set<string>,
  scheduleHasSurveyTeams: boolean,
): boolean {
  if (
    groupPlans.some((plan) => reservedPlanDocumentIds.has(String(plan.documentId ?? '').trim()))
  ) {
    return true;
  }
  if (!scheduleHasSurveyTeams) return false;
  return groupPlans.every((plan) => {
    const schedule = dynamicRelation(plan, 'sampling_schedule');
    return String(schedule?.documentId ?? '').trim() === currentScheduleDocumentId;
  });
}

/** Plan checklist air/non-air di work order, bukan baris sampel quotation. */
function isWorkOrderChecklistPlan(plan: DynamicEntityRow): boolean {
  const airGroup = String(plan.air_group ?? '').trim();
  if (airGroup === 'air' || airGroup === 'non_air') return true;
  const lineIndex = Number(plan.line_index);
  return Number.isFinite(lineIndex) && lineIndex >= 9000;
}

function planSortIndex(plan: DynamicEntityRow, fallback: number): number {
  if (plan.line_index != null && Number.isFinite(Number(plan.line_index))) {
    return Number(plan.line_index);
  }
  return fallback;
}

function sampleLinesFromGroupPlans(groupPlans: DynamicEntityRow[]): SampleLineInGroupDetail[] {
  return groupPlans
    .map((plan, idx) => {
      const sampleLine = dynamicRelation(plan, 'quotation_sample_information');
      const lineIndex = planSortIndex(plan, idx);
      const lineRecord = (sampleLine ?? null) as Record<string, unknown> | null;
      const lineLabel = sampleLineLabel(lineRecord, lineIndex);
      const parameterItems = lineRecord ? parameterItemsFromLine(lineRecord) : [];
      return {
        lineIndex,
        label: lineLabel,
        details: buildSampleLinePickerDetails(lineRecord),
        samplePointCountLabel: samplePointCountLabelFromLine(lineRecord),
        hargaLabel: formatIdrAmount(lineRecord?.harga),
        ...(parameterItems.length ? { parameterItems } : {}),
      };
    })
    .sort((a, b) => a.lineIndex - b.lineIndex);
}

function recurringEventsFromGroup(group: Record<string, unknown> | null): Record<string, unknown>[] {
  if (!group) return [];
  return relationMany(group, 'quotation_recurring_events')
    .filter((event) => String(event.documentId ?? '').trim())
    .sort((a, b) => Number(a.event_index ?? 0) - Number(b.event_index ?? 0));
}

export function buildUnscheduledSampleGroupOptions(
  plans: DynamicEntityRow[],
  currentScheduleDocumentId: string,
  meta: QuotationScheduleGroupMeta,
  reservedPlanDocumentIds: string[] = [],
  scheduleHasSurveyTeams = false,
): UnscheduledSampleGroupOption[] {
  const reserved = new Set(reservedPlanDocumentIds.map((id) => id.trim()).filter(Boolean));
  const groupedPlans = new Map<string, DynamicEntityRow[]>();

  for (let i = 0; i < plans.length; i += 1) {
    const plan = plans[i];
    if (isWorkOrderChecklistPlan(plan)) continue;
    const planDocId = String(plan.documentId ?? '').trim();
    const groupDocId = planGroupDocumentId(plan) || (planDocId ? `flat-${planDocId}` : '');
    if (!groupDocId) continue;
    const list = groupedPlans.get(groupDocId) ?? [];
    list.push(plan);
    groupedPlans.set(groupDocId, list);
  }

  const options: UnscheduledSampleGroupOption[] = [];

  const sortedGroupEntries = [...groupedPlans.entries()].sort((a, b) => {
    const minA = Math.min(...a[1].map((plan, idx) => planSortIndex(plan, idx)));
    const minB = Math.min(...b[1].map((plan, idx) => planSortIndex(plan, idx)));
    return minA - minB;
  });

  const groupIndexById = groupIndexByCreationOrder(
    sortedGroupEntries.map(([documentId, groupPlans]) => {
      const group = planGroupRecord(groupPlans[0]);
      return {
        documentId,
        createdAt: group?.createdAt != null ? String(group.createdAt) : null,
      };
    }),
  );

  sortedGroupEntries.forEach(([groupDocId, groupPlans], fallbackIndex) => {
    const groupIndex = groupIndexById.get(groupDocId) ?? fallbackIndex;
    const group = planGroupRecord(groupPlans[0]);
    const contractEvents = meta.is_contract ? recurringEventsFromGroup(group) : [];
    if (contractEvents.length > 0 && group) {
      const groupLabel = buildGroupLabel(group, groupIndex, meta);
      const sampleLines = sampleLinesFromGroupPlans(groupPlans);
      const groupDetails = buildGroupDetails(group);
      for (const event of contractEvents) {
        const eventId = String(event.documentId ?? '').trim();
        const eventIndex = Number(event.event_index);
        const schedule = dynamicRelation(event as DynamicEntityRow, 'sampling_schedule');
        const scheduleId = String(schedule?.documentId ?? '').trim();
        if (scheduleId && scheduleId === currentScheduleDocumentId && scheduleHasSurveyTeams) continue;
        const planDocumentIds = groupPlans
          .filter((plan) => {
            const sid = planScheduleDocumentId(plan);
            return !sid || (scheduleId && sid === scheduleId);
          })
          .map((plan) => String(plan.documentId ?? '').trim())
          .filter(Boolean);
        options.push({
          quotationGroupDocumentId: groupDocId,
          selectionKey: `${groupDocId}::${eventId}`,
          quotationRecurringEventDocumentId: eventId,
          scheduleDocumentId: scheduleId || undefined,
          label: `${groupLabel} — event ke-${Number.isFinite(eventIndex) ? eventIndex : '?'}`,
          planDocumentIds,
          groupDetails,
          sampleLines,
        });
      }
      return;
    }

    const consultantOnly = Boolean(meta.is_consultant_quote) && !meta.is_contract;
    const boundToCurrentSchedule =
      consultantOnly &&
      Boolean(group) &&
      groupPlans.some((plan) => planScheduleDocumentId(plan) === currentScheduleDocumentId);
    if (boundToCurrentSchedule && group) {
      const sampleLines = sampleLinesFromGroupPlans(groupPlans);
      const planDocumentIds = groupPlans
        .map((plan) => String(plan.documentId ?? '').trim())
        .filter(Boolean);
      options.push({
        quotationGroupDocumentId: groupDocId,
        scheduleDocumentId: currentScheduleDocumentId,
        label: buildGroupLabel(group, groupIndex, meta),
        planDocumentIds,
        groupDetails: buildGroupDetails(group),
        sampleLines,
      });
      return;
    }

    if (
      isGroupHiddenAsScheduled(
        groupPlans,
        currentScheduleDocumentId,
        reserved,
        scheduleHasSurveyTeams,
      )
    ) {
      return;
    }

    const isFlatPseudoGroup = !group;
    if (!isFlatPseudoGroup && !group) return;

    const disabledReason = groupDisabledReason(groupPlans, currentScheduleDocumentId);
    const disabled = Boolean(disabledReason);
    const allAvailable = groupPlans.every((plan) =>
      isPlanAvailableForSchedule(plan, currentScheduleDocumentId, reserved),
    );
    if (!disabled && !allAvailable) return;

    const sampleLines = sampleLinesFromGroupPlans(groupPlans);

    const planDocumentIds = groupPlans
      .map((plan) => String(plan.documentId ?? '').trim())
      .filter(Boolean);

    options.push({
      quotationGroupDocumentId: groupDocId,
      label: isFlatPseudoGroup ? sampleLines[0]?.label ?? `Sample line ${groupIndex + 1}` : buildGroupLabel(group!, groupIndex, meta),
      planDocumentIds,
      groupDetails: isFlatPseudoGroup ? [] : buildGroupDetails(group!),
      sampleLines,
      ...(disabled ? { disabled: true, disabledReason: disabledReason ?? undefined } : {}),
    });
  });

  return options;
}

/** @deprecated Use buildUnscheduledSampleGroupOptions */
export function buildUnscheduledSampleLineOptions(
  plans: DynamicEntityRow[],
  currentScheduleDocumentId: string,
): UnscheduledSampleLineOption[] {
  return buildUnscheduledSampleGroupOptions(plans, currentScheduleDocumentId, {
    is_contract: false,
    is_consultant_quote: false,
  }).flatMap((group) =>
    group.sampleLines.map((line, idx) => ({
      ...group,
      sampleLineDocumentId: `${group.quotationGroupDocumentId}-${idx}`,
      planDocumentId: group.planDocumentIds[idx] ?? group.planDocumentIds[0] ?? '',
      lineIndex: line.lineIndex,
      label: line.label,
      details: line.details,
      ...(line.parameterSimbols?.length ? { parameterSimbols: line.parameterSimbols } : {}),
    })),
  );
}

export function collectPlanDocumentIdsFromPayloads(
  payloads: Array<{ planDocumentId?: string; planDocumentIds?: string[] }>,
): string[] {
  const ids = payloads.flatMap((payload) => {
    if (payload.planDocumentIds?.length) {
      return payload.planDocumentIds.map((id) => id.trim()).filter(Boolean);
    }
    const single = payload.planDocumentId?.trim();
    return single ? [single] : [];
  });
  return [...new Set(ids)];
}

/** Baris sampel yang tampil di tab rencana WO — untuk kontrak/konsultan hanya group yang sudah dijadwalkan (punya plan di WO). */
export function filterSamplesForWorkOrderSamplingPlan<
  T extends { documentId?: string | null; quotation_group?: { documentId?: string } | null },
>(samples: T[], plans: DynamicEntityRow[], usesGroups: boolean): T[] {
  if (!usesGroups) return samples;
  if (!plans.length) return [];

  const scheduledLineDocIds = new Set<string>();
  const scheduledGroupDocIds = new Set<string>();

  for (const plan of plans) {
    const lineDocId = String(dynamicRelation(plan, 'quotation_sample_information')?.documentId ?? '').trim();
    if (lineDocId) scheduledLineDocIds.add(lineDocId);

    const groupDocId = planGroupDocumentId(plan);
    if (groupDocId) scheduledGroupDocIds.add(groupDocId);
  }

  return samples.filter((line) => {
    const lineDocId = line.documentId?.trim();
    if (lineDocId && scheduledLineDocIds.has(lineDocId)) return true;
    const groupDocId = line.quotation_group?.documentId?.trim();
    return Boolean(groupDocId && scheduledGroupDocIds.has(groupDocId));
  });
}
